"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.platformConfigDto = exports.reactivateCompanyRequest = exports.suspendCompanyRequest = exports.updatePlatformCompanyRequest = exports.createPlatformCompanyRequest = exports.platformCompanyDetail = exports.platformCompanyDto = exports.maxUsers = exports.planCode = exports.companyStatus = void 0;
exports.isCompanyReadOnly = isCompanyReadOnly;
const zod_1 = require("zod");
const common_1 = require("./common");
const auth_1 = require("./auth");
// ---------------------------------------------------------------------------
// لوحة المنصة المشتركة — مشغّل يدير الشركات واشتراكها (PLATFORM_MODE)
// ---------------------------------------------------------------------------
/** حالة الاشتراك المخزَّنة. الإيقاف لا يحذف شيئًا: القراءة والتصدير باقيان. */
exports.companyStatus = zod_1.z.enum(['trial', 'active', 'suspended']);
/**
 * هل الكتابة على الشركة ممنوعة الآن؟ مُشتق لا مخزَّن: الموقوفة ممنوعة، والتجربة
 * ممنوعة بعد نهايتها حتى تُفعَّل — فانتهاء التجربة لا يحتاج مهمة مجدولة تقلب الحالة.
 */
function isCompanyReadOnly(company, now) {
    if (company.status === 'suspended')
        return true;
    if (company.status === 'trial' && company.trialEndsAt !== null) {
        return new Date(company.trialEndsAt).getTime() <= now.getTime();
    }
    return false;
}
/** رمز الباقة: نص حر قصير — الباقات وأسعارها قرار تجارى لا قائمة فى الكود. */
exports.planCode = zod_1.z
    .string()
    .trim()
    .min(1)
    .max(32)
    .regex(/^[a-z0-9][a-z0-9_-]*$/, 'plan code: lowercase letters, digits, - or _');
exports.maxUsers = zod_1.z.number().int().min(1).max(10_000);
/**
 * الشركة كما يراها المشغّل: هويتها واشتراكها ومؤشرات نشاط فقط. **لا مبلغ مالى**
 * ولا رصيد — المشغّل ليس عضوًا، ودفتر الشركة ليس من شأنه.
 */
exports.platformCompanyDto = zod_1.z.object({
    id: common_1.uuid,
    legalNameAr: zod_1.z.string(),
    legalNameEn: zod_1.z.string().nullable(),
    taxRegistrationNumber: zod_1.z.string().nullable(),
    status: exports.companyStatus,
    readOnly: zod_1.z.boolean(),
    plan: zod_1.z.string().nullable(),
    trialEndsAt: zod_1.z.string().nullable(),
    maxUsers: zod_1.z.number().int().nullable(),
    suspendedAt: zod_1.z.string().nullable(),
    suspendedReason: zod_1.z.string().nullable(),
    createdAt: zod_1.z.string(),
    userCount: zod_1.z.number().int(),
    journalCount: zod_1.z.number().int(),
    /** آخر قيد أو آخر دخول لعضو — أيهما أحدث. */
    lastActivityAt: zod_1.z.string().nullable(),
});
exports.platformCompanyDetail = exports.platformCompanyDto.extend({
    owners: zod_1.z.array(zod_1.z.object({
        userId: common_1.uuid,
        email: zod_1.z.string(),
        displayName: zod_1.z.string(),
        lastLoginAt: zod_1.z.string().nullable(),
    })),
});
exports.createPlatformCompanyRequest = zod_1.z
    .object({
    legalNameAr: zod_1.z.string().trim().min(1).max(200),
    legalNameEn: zod_1.z.string().trim().min(1).max(200).nullable().optional(),
    taxRegistrationNumber: zod_1.z.string().trim().min(1).max(32).nullable().optional(),
    currency: common_1.currencyCode.default('EGP'),
    fiscalYearStart: common_1.isoDate,
    fiscalYearEnd: common_1.isoDate,
    status: zod_1.z.enum(['trial', 'active']),
    plan: exports.planCode.nullable().optional(),
    /** نهاية التجربة — إلزامية للتجربة، وتُملأ فى الواجهة من مدة التجربة الافتراضية. */
    trialEndsAt: common_1.isoDate.nullable().optional(),
    maxUsers: exports.maxUsers.nullable().optional(),
    /**
     * المالك الأول: حساب جديد بكلمة سر أولى يسلّمها المشغّل للعميل. لا تُعاد ولا
     * تُسجَّل، والمالك مُلزَم بالعامل الثانى من أول دخول.
     */
    owner: zod_1.z
        .object({
        email: auth_1.email,
        displayName: zod_1.z.string().trim().min(1).max(100),
        password: auth_1.password,
    })
        .strict(),
})
    .strict()
    .refine((body) => body.fiscalYearEnd > body.fiscalYearStart, {
    message: 'fiscalYearEnd must be after fiscalYearStart',
    path: ['fiscalYearEnd'],
})
    .refine((body) => body.status !== 'trial' || Boolean(body.trialEndsAt), {
    message: 'a trial needs trialEndsAt',
    path: ['trialEndsAt'],
});
/** تعديل الاشتراك — الحالة لا تتغير هنا، بل بمسارى الإيقاف وإعادة التفعيل. */
exports.updatePlatformCompanyRequest = zod_1.z
    .object({
    plan: exports.planCode.nullable().optional(),
    trialEndsAt: common_1.isoDate.nullable().optional(),
    maxUsers: exports.maxUsers.nullable().optional(),
})
    .strict()
    .refine((body) => Object.keys(body).length > 0, 'nothing to update');
exports.suspendCompanyRequest = zod_1.z
    .object({ reason: zod_1.z.string().trim().min(1).max(500) })
    .strict();
/** إعادة التفعيل: إلى `active`، أو إلى تجربة ممدَّدة بتاريخ نهاية جديد. */
exports.reactivateCompanyRequest = zod_1.z
    .object({
    status: zod_1.z.enum(['trial', 'active']).default('active'),
    trialEndsAt: common_1.isoDate.nullable().optional(),
})
    .strict()
    .refine((body) => body.status !== 'trial' || Boolean(body.trialEndsAt), {
    message: 'a trial needs trialEndsAt',
    path: ['trialEndsAt'],
});
/** إعدادات المنصة التجارية كما تقرؤها الواجهة لتملأ النموذج — كلها من البيئة. */
exports.platformConfigDto = zod_1.z.object({
    trialDays: zod_1.z.number().int(),
    defaultMaxUsers: zod_1.z.number().int().nullable(),
    plans: zod_1.z.array(zod_1.z.string()),
});
//# sourceMappingURL=platform.js.map